import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl-next';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';

import { getExtensionManifest } from '@atlassian/company-hub-common';
import type { CustomSitesExtensionProviderProps } from '@atlassian/company-hub-common';

import { LoadableAfterPaint } from '@confluence/loadable';

import { CAROUSEL_VERSION_1, CAROUSEL_VERSION_2 } from './carouselVersion';
import { carouselExtensionKey, carouselExtensionType } from './carouselConstants';
import { CommonLayout } from './carouselTypes';
import type { CarouselParameters } from './carouselTypes';
import {
	createNewFrameWithDefaultParameters,
	carouselNewParametersV2,
	FRAME_V2_DEFAULT_IMAGE_SRC,
} from './CarouselConfigPanel/useCarouselExtensionParametersAndActions';

const CarouselExtension = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CarouselExtension" */ './CarouselExtension'))
			.CarouselExtension,
});

const slideImages = [
	{
		imageSrc:
			'https://images.unsplash.com/photo-1615716272018-7c5b1216e262?auto=format&fit=crop&q=80&w=1080',
		imageAltText: '',
	},
	{
		imageSrc:
			'https://images.unsplash.com/photo-1615715757448-f8e0cdccf1e2?auto=format&fit=crop&q=80&w=1080',
		imageAltText: '',
	},
	{
		imageSrc:
			'https://images.unsplash.com/photo-1615716038858-2f9a60c330a6?auto=format&fit=crop&q=80&w=1080',
		imageAltText: '',
	},
];

const i18n = defineMessages({
	carouselMacroTitle: {
		id: 'custom-sites-extensions.carousel.manifest.macro-title',
		defaultMessage: 'Carousel',
		description: 'Title of carousel macro to be displayed in toolbar/shortcut macros dropdown.',
	},
	carouselMacroTitleUpdated: {
		id: 'custom-sites-extensions.carousel.manifest.macro-title.updated',
		defaultMessage: 'Carousel [Updated]',
		description:
			'New title of carousel macro with additional copy about the feature being updated. To be displayed in toolbar/shortcut macros dropdown.',
	},
	carouselMacroDescription: {
		id: 'custom-sites-extensions.carousel.manifest.macro-description',
		defaultMessage: 'Create a rotating trio of dynamic frames.',
		description:
			'Description of carousel macro to be displayed in toolbar/shortcut macros dropdown',
	},
});

const getV1Frames = (intl: IntlShape) => {
	return slideImages.map(({ imageSrc, imageAltText }) =>
		createNewFrameWithDefaultParameters(false, intl, { imageSrc, imageAltText }),
	);
};

const carouselFrames = [
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: CommonLayout.SPLIT_IMAGE_LEFT,
	},
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: CommonLayout.FRAME_ABOVE_IMAGE,
	},
	{
		imageSrc: FRAME_V2_DEFAULT_IMAGE_SRC,
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
	},
];
const getV2Frames = (intl: IntlShape) => {
	return carouselFrames.map(({ imageSrc, layout }) =>
		createNewFrameWithDefaultParameters(true, intl, { imageSrc, layout }),
	);
};

export const carouselParameters = (
	extensionTitle: string,
	intl: IntlShape,
	isCarouselV2Enabled = false,
) =>
	isCarouselV2Enabled
		? {
				cards: getV2Frames(intl),
				extensionTitle,
				version: CAROUSEL_VERSION_2,
				...carouselNewParametersV2,
			}
		: {
				cards: getV1Frames(intl),
				extensionTitle,
				version: CAROUSEL_VERSION_1,
			};

export const carouselExtensionProvider = async ({
	contentId,
	editorActions,
	intl,
	openConfigPanel,
	setUpsellModalSource,
}: CustomSitesExtensionProviderProps) => {
	const title = fg('company_hub_carousel_v2')
		? intl.formatMessage(i18n.carouselMacroTitleUpdated)
		: intl.formatMessage(i18n.carouselMacroTitle);
	const type = carouselExtensionType;
	const description = intl.formatMessage(i18n.carouselMacroDescription);
	const render =
		async () =>
		({ node }) => {
			return <CarouselExtension extensionNode={node} contentId={contentId} />;
		};

	const icons = {
		'48': () =>
			import(
				/* webpackChunkName: "loadable-CarouselMacroIcon" */
				'@confluence/icons/entry-points/CarouselMacroIcon'
			).then((mod) => mod.CarouselMacroIcon),
	};

	const parameters = carouselParameters(
		intl.formatMessage(i18n.carouselMacroTitle),
		intl,
		fg('company_hub_carousel_v2'),
	) as CarouselParameters;

	const update = async () => {
		const selectedNode = editorActions?.getSelectedNode()?.toJSON();
		const selectedLocalId = selectedNode?.attrs?.localId;

		if (!!selectedLocalId && openConfigPanel) {
			openConfigPanel(selectedLocalId);
		}
	};

	return new DefaultExtensionProvider([
		getExtensionManifest<CarouselParameters>({
			key: carouselExtensionKey,
			icons,
			parameters,
			update,
			render,
			title,
			type,
			description,
			setUpsellModalSource,
			source: 'premiumCarouselInsert',
		}),
	]);
};
